import React, { useState, useEffect, useRef } from 'react';
import { useAIModel } from '../contexts/AIModelContext';
import ModelSelector from './ModelSelector';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { 
  RefreshCw, TrendingUp, TrendingDown, Shield, 
  Calendar, Target, AlertTriangle, CheckCircle2,
  Repeat, Brain, Zap, Award, Plus, Trash2, Layers, Timer, CandlestickChart
} from 'lucide-react';
import { toast } from 'sonner';
import TradingChartModal from './TradingChartModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Strategy templates for quick selection
const STRATEGY_TEMPLATES = [
  { id: 'single', name: 'Single Leg', legs: 1 },
  { id: 'vertical_spread', name: 'Vertical Spread', legs: 2 },
  { id: 'iron_condor', name: 'Iron Condor', legs: 4 },
  { id: 'straddle', name: 'Straddle/Strangle', legs: 2 },
  { id: 'custom', name: 'Custom Multi-Leg', legs: 0 },
];

const createEmptyLeg = () => ({
  option_type: 'call',
  position_type: 'long',
  strike: '',
  quantity: 1,
  entry_premium: '',
  current_premium: ''
});

const Rollmaster = ({ initialPosition }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [fetchingPremiums, setFetchingPremiums] = useState(false);
  const [currentPrice, setCurrentPrice] = useState(null);
  
  // Chart modal state
  const [isChartOpen, setIsChartOpen] = useState(false);
  
  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [lastRefresh, setLastRefresh] = useState(null);
  const autoRefreshRef = useRef(null);
  
  // Common position info
  const [symbol, setSymbol] = useState('');
  const [expiration, setExpiration] = useState('');
  const [strategyType, setStrategyType] = useState('single');
  
  // Multi-leg support
  const [legs, setLegs] = useState([createEmptyLeg()]);
  
  const [goal, setGoal] = useState('improve');
  const [riskTolerance, setRiskTolerance] = useState('moderate');
  const { model: aiModel, setModel: setAiModel } = useAIModel();

  // Load initial position from portfolio
  useEffect(() => {
    if (!initialPosition) return;
    
    console.log('Rollmaster: Loading initial position:', initialPosition);
    
    // Check if this is a spread (multiple legs) or single position
    const isSpread = initialPosition.is_spread || initialPosition.legs?.length > 1;
    
    if (isSpread && initialPosition.legs) {
      // Handle spread/multi-leg position
      setSymbol(initialPosition.underlying_symbol || '');
      
      // Map the strategy type to our template IDs
      const spreadType = initialPosition.strategy_type;
      let templateType = 'custom'; // Default to custom for unknown spreads
      
      if (spreadType === 'vertical_spread') {
        templateType = 'vertical_spread';
      } else if (spreadType === 'iron_condor') {
        templateType = 'iron_condor';
      } else if (spreadType === 'straddle' || spreadType === 'strangle') {
        templateType = 'straddle';
      } else if (initialPosition.legs.length === 1) {
        templateType = 'single';
      }
      
      setStrategyType(templateType);
      
      const expDate = initialPosition.expiration_date || initialPosition.legs[0]?.expiration_date;
      if (expDate) {
        const dateStr = typeof expDate === 'string' ? expDate.split('T')[0] : new Date(expDate).toISOString().split('T')[0];
        setExpiration(dateStr);
      }
      
      const newLegs = initialPosition.legs.map(leg => {
        console.log('Rollmaster: Processing leg:', leg);
        return {
          option_type: leg.option_type || 'call',
          position_type: leg.quantity > 0 ? 'long' : 'short',
          strike: leg.strike_price?.toString() || leg.strike?.toString() || '',
          quantity: Math.abs(leg.quantity) || 1,
          entry_premium: leg.average_open_price?.toString() || leg.entry_premium?.toString() || '',
          current_premium: leg.close_price?.toString() || leg.current_premium?.toString() || ''
        };
      });
      
      console.log('Rollmaster: Setting legs:', newLegs);
      setLegs(newLegs);
      setCurrentPrice(initialPosition.legs[0]?.close_price || initialPosition.legs[0]?.current_premium);
      
      // Clear previous result
      setResult(null);
      
    } else if (initialPosition.is_option) {
      // Handle single-leg option
      setSymbol(initialPosition.underlying_symbol || '');
      setStrategyType('single');
      
      if (initialPosition.expiration_date) {
        const expDate = initialPosition.expiration_date;
        const dateStr = typeof expDate === 'string' ? expDate.split('T')[0] : new Date(expDate).toISOString().split('T')[0];
        setExpiration(dateStr);
      }
      
      const newLeg = {
        option_type: initialPosition.option_type || 'call',
        position_type: initialPosition.quantity > 0 ? 'long' : 'short',
        strike: initialPosition.strike_price?.toString() || '',
        quantity: Math.abs(initialPosition.quantity) || 1,
        entry_premium: initialPosition.average_open_price?.toString() || '',
        current_premium: initialPosition.close_price?.toString() || ''
      };
      setLegs([newLeg]);
      setCurrentPrice(initialPosition.close_price);
      
      // Clear previous result
      setResult(null);
    }
  }, [initialPosition]);

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefresh && symbol && expiration && legs.some(l => l.strike)) {
      fetchCurrentPremiums(true);
      
      autoRefreshRef.current = setInterval(() => {
        fetchCurrentPremiums(true);
      }, refreshInterval * 1000);
      
      return () => {
        if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
      };
    } else {
      if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    }
  }, [autoRefresh, refreshInterval, symbol, expiration]);

  // Fast premium fetching function
  const fetchCurrentPremiums = async (silent = false) => {
    if (!symbol || !expiration) {
      if (!silent) toast.error('Please enter symbol and expiration first');
      return;
    }
    
    const legsWithStrikes = legs.filter(leg => leg.strike);
    if (legsWithStrikes.length === 0) {
      if (!silent) toast.error('Please enter at least one strike price');
      return;
    }
    
    if (!silent) setFetchingPremiums(true);
    if (!silent) toast.info('Fetching current premiums...');
    
    try {
      const response = await axios.post(`${API}/data/premiums`, {
        symbol: symbol.toUpperCase(),
        expiration: expiration,
        legs: legsWithStrikes.map(leg => ({
          strike: parseFloat(leg.strike),
          option_type: leg.option_type
        }))
      }, { timeout: 15000 });
      
      // Create a map of fetched premiums
      const premiumMap = {};
      response.data.legs.forEach(leg => {
        if (leg.premium) {
          premiumMap[`${leg.strike}_${leg.option_type}`] = leg.premium;
        }
      });
      
      // Update legs with fetched current premiums
      const updatedLegs = legs.map(leg => {
        const key = `${leg.strike}_${leg.option_type}`;
        if (premiumMap[key]) {
          return { ...leg, current_premium: premiumMap[key].toFixed(2) };
        }
        return leg;
      });
      
      setLegs(updatedLegs);
      setCurrentPrice(response.data.current_price);
      setLastRefresh(new Date());
      
      const found = response.data.legs.filter(l => l.found).length;
      if (!silent) toast.success(`Fetched ${found}/${legsWithStrikes.length} premiums. Stock: $${response.data.current_price}`);
    } catch (error) {
      console.error('Error fetching premiums:', error);
      if (!silent) toast.error('Failed to fetch premiums. Please enter manually.');
    } finally {
      if (!silent) setFetchingPremiums(false);
    }
  };

  // Add a new leg
  const addLeg = () => {
    if (legs.length < 6) {
      setLegs([...legs, createEmptyLeg()]);
    } else {
      toast.error('Maximum 6 legs allowed');
    }
  };

  // Remove a leg
  const removeLeg = (index) => {
    if (legs.length > 1) {
      setLegs(legs.filter((_, i) => i !== index));
    }
  };

  // Update a specific leg
  const updateLeg = (index, field, value) => {
    const newLegs = [...legs];
    newLegs[index] = { ...newLegs[index], [field]: value };
    setLegs(newLegs);
  };

  // Apply strategy template
  const applyTemplate = (templateId) => {
    setStrategyType(templateId);
    switch (templateId) {
      case 'single':
        setLegs([createEmptyLeg()]);
        break;
      case 'vertical_spread':
        setLegs([
          { option_type: 'call', position_type: 'long', strike: '', quantity: 1, entry_premium: '', current_premium: '' },
          { option_type: 'call', position_type: 'short', strike: '', quantity: 1, entry_premium: '', current_premium: '' },
        ]);
        break;
      case 'iron_condor':
        setLegs([
          { option_type: 'put', position_type: 'long', strike: '', quantity: 1, entry_premium: '', current_premium: '' },
          { option_type: 'put', position_type: 'short', strike: '', quantity: 1, entry_premium: '', current_premium: '' },
          { option_type: 'call', position_type: 'short', strike: '', quantity: 1, entry_premium: '', current_premium: '' },
          { option_type: 'call', position_type: 'long', strike: '', quantity: 1, entry_premium: '', current_premium: '' },
        ]);
        break;
      case 'straddle':
        setLegs([
          { option_type: 'call', position_type: 'long', strike: '', quantity: 1, entry_premium: '', current_premium: '' },
          { option_type: 'put', position_type: 'long', strike: '', quantity: 1, entry_premium: '', current_premium: '' },
        ]);
        break;
      case 'custom':
        // Keep current legs for custom
        break;
      default:
        setLegs([createEmptyLeg()]);
    }
  };

  const analyzePosition = async () => {
    // Validate inputs
    if (!symbol) {
      toast.error('Please enter a stock symbol');
      return;
    }
    if (!expiration) {
      toast.error('Please select an expiration date');
      return;
    }
    
    // Validate legs
    for (let i = 0; i < legs.length; i++) {
      const leg = legs[i];
      if (!leg.strike || !leg.entry_premium) {
        toast.error(`Please fill in strike and entry premium for Leg ${i + 1}`);
        return;
      }
    }
    
    setLoading(true);
    toast.info('Analyzing your position and finding optimal roll strategies...');
    
    try {
      const response = await axios.post(`${API}/rollmaster/analyze`, {
        symbol: symbol.toUpperCase(),
        expiration,
        legs: legs.map(leg => ({
          option_type: leg.option_type,
          position_type: leg.position_type,
          strike: parseFloat(leg.strike),
          quantity: parseInt(leg.quantity) || 1,
          entry_premium: parseFloat(leg.entry_premium),
          current_premium: leg.current_premium ? parseFloat(leg.current_premium) : null
        })),
        strategy_type: strategyType,
        goal,
        risk_tolerance: riskTolerance,
        ai_model: aiModel
      });
      
      const data = response.data;
      if (data.status === 'rate_limited') {
        toast.error(data.message || 'Market-data provider is temporarily rate-limited. Please try again shortly.');
        return;
      }
      setResult(data);
      if (data.candidate_status === 'insufficient_data') {
        toast.error('Market quotes are missing for this position — see the details below.');
      } else if (data.status === 'partial') {
        toast.warning(data.ai_error || 'Showing deterministic position analysis; AI suggestions are temporarily unavailable.');
      } else if (data.candidate_status === 'no_viable_candidates') {
        toast.warning('No roll candidate met the current criteria.');
      } else {
        toast.success('Roll strategies generated!');
      }
    } catch (error) {
      console.error('Error analyzing position:', error);
      const status = error.response?.status;
      let msg = error.response?.data?.detail || 'Failed to analyze position';
      if (status === 429) msg = 'Market-data provider is temporarily rate-limited. Please try again shortly.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const getRiskBadge = (risk) => {
    switch (risk?.toLowerCase()) {
      case 'low':
        return <Badge className="bg-green-100 text-green-800">Low Risk</Badge>;
      case 'medium':
        return <Badge className="bg-yellow-100 text-yellow-800">Medium Risk</Badge>;
      case 'high':
        return <Badge className="bg-red-100 text-red-800">High Risk</Badge>;
      default:
        return <Badge variant="outline">{risk}</Badge>;
    }
  };

  const getAssignmentRiskColor = (risk) => {
    switch (risk?.toLowerCase()) {
      case 'low': return 'text-green-600';
      case 'medium': return 'text-yellow-600';
      case 'high': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getStrategyDescription = () => {
    const longLegs = legs.filter(l => l.position_type === 'long').length;
    const shortLegs = legs.filter(l => l.position_type === 'short').length;
    const calls = legs.filter(l => l.option_type === 'call').length;
    const puts = legs.filter(l => l.option_type === 'put').length;
    
    return `${legs.length} leg${legs.length > 1 ? 's' : ''}: ${longLegs} long, ${shortLegs} short | ${calls} call${calls !== 1 ? 's' : ''}, ${puts} put${puts !== 1 ? 's' : ''}`;
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="glass-effect border-0 shadow-lg bg-gradient-to-r from-purple-50 to-indigo-50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl">
                <Repeat className="h-8 w-8 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl">Rollmaster</CardTitle>
                <CardDescription className="text-base">
                  AI-powered option rolling advisor for single & multi-leg strategies
                </CardDescription>
              </div>
            </div>
            {/* Auto-refresh toggle */}
            <div className="flex items-center space-x-4 bg-white/50 rounded-lg px-4 py-2">
              <div className="flex items-center space-x-2">
                <Timer className={`h-4 w-4 ${autoRefresh ? 'text-green-600 animate-pulse' : 'text-gray-400'}`} />
                <Label htmlFor="auto-refresh-rm" className="text-sm font-medium cursor-pointer">
                  Auto-refresh
                </Label>
                <Switch
                  id="auto-refresh-rm"
                  checked={autoRefresh}
                  onCheckedChange={setAutoRefresh}
                  data-testid="auto-refresh-toggle-rollmaster"
                />
              </div>
              {autoRefresh && (
                <Select value={String(refreshInterval)} onValueChange={(v) => setRefreshInterval(Number(v))}>
                  <SelectTrigger className="w-20 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15s</SelectItem>
                    <SelectItem value="30">30s</SelectItem>
                    <SelectItem value="60">1m</SelectItem>
                    <SelectItem value="120">2m</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {autoRefresh && lastRefresh && (
                <span className="text-xs text-gray-500">
                  Last: {lastRefresh.toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Input Form */}
      <Card className="glass-effect border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Target className="h-5 w-5 mr-2 text-indigo-600" />
            Your Current Position
          </CardTitle>
          <CardDescription>Enter your option position details (single or multi-leg)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Row 1: Symbol, Expiration, Strategy Type */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Stock Symbol *</Label>
              <div className="flex space-x-2">
                <Input
                  placeholder="e.g., AAPL"
                  value={symbol}
                  onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                  className="text-lg font-semibold"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => symbol && setIsChartOpen(true)}
                  disabled={!symbol}
                  className="px-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white border-0"
                  title="View Live Chart"
                  data-testid="view-chart-btn"
                >
                  <CandlestickChart className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div>
              <Label>Expiration Date *</Label>
              <Input
                type="date"
                value={expiration}
                onChange={(e) => setExpiration(e.target.value)}
              />
            </div>
            <div>
              <Label>Strategy Template</Label>
              <Select value={strategyType} onValueChange={applyTemplate}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STRATEGY_TEMPLATES.map(template => (
                    <SelectItem key={template.id} value={template.id}>
                      <span className="flex items-center">
                        <Layers className="h-4 w-4 mr-2 text-indigo-600" />
                        {template.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Legs Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold flex items-center">
                <Layers className="h-4 w-4 mr-2 text-indigo-600" />
                Position Legs ({legs.length})
                {currentPrice && (
                  <Badge className="ml-3 bg-indigo-100 text-indigo-700">${currentPrice}</Badge>
                )}
              </Label>
              <div className="flex space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fetchCurrentPremiums(false)}
                  disabled={fetchingPremiums || !symbol || !expiration}
                  className="text-blue-600 border-blue-300 hover:bg-blue-50"
                  data-testid="fetch-premiums-btn"
                >
                  {fetchingPremiums ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
                  Fetch Premiums
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addLeg}
                  disabled={legs.length >= 6}
                  className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Leg
                </Button>
              </div>
            </div>

            {/* Strategy Summary */}
            <div className="bg-slate-50 rounded-lg px-4 py-2 text-sm text-gray-600">
              {getStrategyDescription()}
            </div>

            {/* Legs Grid */}
            <div className="space-y-3">
              {legs.map((leg, index) => (
                <div key={index} className="bg-gradient-to-r from-slate-50 to-indigo-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-indigo-700">Leg {index + 1}</span>
                    {legs.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLeg(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                    <div>
                      <Label className="text-xs">Type</Label>
                      <Select 
                        value={leg.option_type} 
                        onValueChange={(v) => updateLeg(index, 'option_type', v)}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="call">
                            <span className="flex items-center">
                              <TrendingUp className="h-3 w-3 mr-1 text-green-600" /> Call
                            </span>
                          </SelectItem>
                          <SelectItem value="put">
                            <span className="flex items-center">
                              <TrendingDown className="h-3 w-3 mr-1 text-red-600" /> Put
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Position</Label>
                      <Select 
                        value={leg.position_type} 
                        onValueChange={(v) => updateLeg(index, 'position_type', v)}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="long">Long (Buy)</SelectItem>
                          <SelectItem value="short">Short (Sell)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Strike *</Label>
                      <Input
                        type="number"
                        step="0.5"
                        placeholder="150"
                        value={leg.strike}
                        onChange={(e) => updateLeg(index, 'strike', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Qty</Label>
                      <Input
                        type="number"
                        min="1"
                        value={leg.quantity}
                        onChange={(e) => updateLeg(index, 'quantity', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Entry Premium *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="2.50"
                        value={leg.entry_premium}
                        onChange={(e) => updateLeg(index, 'entry_premium', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Current (opt)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Auto"
                        value={leg.current_premium}
                        onChange={(e) => updateLeg(index, 'current_premium', e.target.value)}
                        className="h-9"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Row 3: Goal and Risk Tolerance */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-r from-slate-50 to-indigo-50 rounded-lg p-4">
              <Label className="text-sm font-semibold mb-3 block">What's your goal?</Label>
              <RadioGroup value={goal} onValueChange={setGoal} className="space-y-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="improve" id="improve" />
                  <label htmlFor="improve" className="text-sm cursor-pointer">Improve position / More profit potential</label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="avoid_assignment" id="avoid_assignment" />
                  <label htmlFor="avoid_assignment" className="text-sm cursor-pointer">Avoid assignment (short options)</label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="cut_loss" id="cut_loss" />
                  <label htmlFor="cut_loss" className="text-sm cursor-pointer">Cut losses / Reduce risk</label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="take_profit" id="take_profit" />
                  <label htmlFor="take_profit" className="text-sm cursor-pointer">Take profits / Lock in gains</label>
                </div>
              </RadioGroup>
            </div>

            <div className="bg-gradient-to-r from-slate-50 to-purple-50 rounded-lg p-4">
              <Label className="text-sm font-semibold mb-3 block">Risk Tolerance</Label>
              <RadioGroup value={riskTolerance} onValueChange={setRiskTolerance} className="space-y-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="conservative" id="conservative" />
                  <label htmlFor="conservative" className="text-sm cursor-pointer">Conservative - Minimize risk, smaller gains</label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="moderate" id="moderate" />
                  <label htmlFor="moderate" className="text-sm cursor-pointer">Moderate - Balanced risk/reward</label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="aggressive" id="aggressive" />
                  <label htmlFor="aggressive" className="text-sm cursor-pointer">Aggressive - Higher risk for higher reward</label>
                </div>
              </RadioGroup>
            </div>
          </div>

          {/* AI Model Selection */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Brain className="h-5 w-5 text-indigo-600" />
              <ModelSelector />
            </div>
            
            <Button
              onClick={analyzePosition}
              disabled={loading}
              data-testid="rollmaster-analyze-btn"
              className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 px-8 py-6 text-lg"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Zap className="h-5 w-5 mr-2" />
                  Find Roll Strategies
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <div className="space-y-6 fade-in">
          {/* Position Summary */}
          <Card className="glass-effect border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center">
                  <Shield className="h-5 w-5 mr-2 text-indigo-600" />
                  Position Analysis
                </span>
                <Badge variant="outline" className="text-lg">
                  {result.symbol} @ ${result.current_price}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Multi-leg summary */}
              {result.legs_summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-600">Total Legs</p>
                    <p className="text-lg font-bold">{result.legs_summary.total_legs}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-600">Strategy Type</p>
                    <p className="text-lg font-bold capitalize">{result.strategy_type?.replace('_', ' ')}</p>
                  </div>
                  <div className={`rounded-lg p-3 text-center ${result.legs_summary.total_pl >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                    <p className="text-xs text-gray-600">Total P/L</p>
                    <p className={`text-lg font-bold ${result.legs_summary.total_pl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {result.legs_summary.total_pl >= 0 ? '+' : ''}${result.legs_summary.total_pl?.toFixed(2)}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-600">DTE</p>
                    <p className="text-lg font-bold">{result.legs_summary.dte} days</p>
                  </div>
                </div>
              )}

              {/* Individual Legs */}
              {result.legs && result.legs.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Position Breakdown:</p>
                  <div className="grid gap-2">
                    {result.legs.map((leg, i) => (
                      <div key={i} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2 text-sm">
                        <div className="flex items-center space-x-3">
                          <span className={`font-semibold ${leg.position_type === 'long' ? 'text-green-600' : 'text-red-600'}`}>
                            {leg.position_type.toUpperCase()}
                          </span>
                          <span className="text-gray-600">{leg.quantity}x</span>
                          <span className="font-medium">${leg.strike} {leg.option_type.toUpperCase()}</span>
                        </div>
                        <div className="flex items-center space-x-4">
                          <span className="text-gray-600">Entry: ${leg.entry_premium}</span>
                          <span className="text-gray-600">Current: ${leg.current_premium?.toFixed(2)}</span>
                          <span className={leg.leg_pl >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                            {leg.leg_pl >= 0 ? '+' : ''}${leg.leg_pl?.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {result.analysis && (
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4">
                  <p className="text-sm text-gray-700">{result.analysis}</p>
                </div>
              )}
              
              {result.recommendation_summary && (
                <div className="mt-4 bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <p className="text-sm font-semibold text-blue-800 mb-1">💡 Recommendation</p>
                  <p className="text-sm text-blue-700">{result.recommendation_summary}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Roll Strategies */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold flex items-center">
              <Award className="h-5 w-5 mr-2 text-yellow-500" />
              Top 3 Roll Strategies
            </h3>
            
            {result.roll_strategies && result.roll_strategies.length > 0 ? (
              <div className="grid gap-4">
                {result.roll_strategies.map((strategy, index) => (
                  <Card key={index} className={`glass-effect border-0 shadow-lg overflow-hidden ${
                    index === 0 ? 'ring-2 ring-yellow-400' : ''
                  }`}>
                    <div className={`h-1 ${
                      index === 0 ? 'bg-gradient-to-r from-yellow-400 to-amber-500' :
                      index === 1 ? 'bg-gradient-to-r from-gray-300 to-gray-400' :
                      'bg-gradient-to-r from-amber-600 to-amber-700'
                    }`} />
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                            index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : 'bg-amber-600'
                          }`}>
                            {index + 1}
                          </div>
                          <div>
                            <CardTitle className="text-lg">{strategy.name}</CardTitle>
                            <CardDescription>{strategy.action}</CardDescription>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {getRiskBadge(strategy.risk_level)}
                          {strategy.best_for && (
                            <Badge variant="outline" className="bg-indigo-50">
                              {strategy.best_for}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* New Position Details */}
                      {strategy.new_legs && strategy.new_legs.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs font-semibold text-gray-500 mb-2">NEW POSITION:</p>
                          <div className="grid gap-1">
                            {strategy.new_legs.map((leg, i) => (
                              <div key={i} className="flex items-center text-sm bg-indigo-50 rounded px-3 py-1">
                                <span className={`font-semibold mr-2 ${leg.position_type === 'long' ? 'text-green-600' : 'text-red-600'}`}>
                                  {leg.position_type?.toUpperCase()}
                                </span>
                                <span>${leg.strike} {leg.option_type?.toUpperCase()}</span>
                                {leg.premium != null && (
                                  <span className="ml-2 text-xs text-gray-500" data-testid="roll-leg-quote">
                                    @ ${Number(leg.premium).toFixed(2)} ({leg.position_type === 'short' ? 'bid' : 'ask'})
                                  </span>
                                )}
                                <span className="ml-auto text-gray-600">{strategy.new_expiration}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                        <div className="bg-slate-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-600">New Expiration</p>
                          <p className="text-sm font-bold">{strategy.new_expiration}</p>
                        </div>
                        <div className={`rounded-lg p-3 text-center ${
                          strategy.net_credit_debit >= 0 ? 'bg-green-50' : 'bg-orange-50'
                        }`}>
                          <p className="text-xs text-gray-600">Net {strategy.net_credit_debit >= 0 ? 'Credit' : 'Debit'}</p>
                          <p className={`text-lg font-bold ${
                            strategy.net_credit_debit >= 0 ? 'text-green-600' : 'text-orange-600'
                          }`}>
                            ${Math.abs(strategy.net_credit_debit || 0).toFixed(2)}
                          </p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-600">Max Profit</p>
                          <p className="text-lg font-bold text-green-600">${strategy.max_profit}</p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-600">Max Loss</p>
                          <p className="text-lg font-bold text-red-600">${strategy.max_loss}</p>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-600">Success Prob.</p>
                          <p className="text-lg font-bold text-blue-600">{strategy.probability_success}%</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="flex items-center space-x-2 text-sm">
                          <Target className="h-4 w-4 text-indigo-600" />
                          <span className="text-gray-600">New Break-Even:</span>
                          <span className="font-semibold">${strategy.new_breakeven}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-sm">
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                          <span className="text-gray-600">Max Loss:</span>
                          <span className="font-semibold text-red-600">${strategy.max_loss}</span>
                        </div>
                      </div>
                      
                      {strategy.rationale && (
                        <div className="bg-gradient-to-r from-slate-50 to-indigo-50 rounded-lg p-3">
                          <p className="text-sm text-gray-700">
                            <CheckCircle2 className="h-4 w-4 inline mr-1 text-green-600" />
                            {strategy.rationale}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="glass-effect border-0 shadow-lg" data-testid="rollmaster-empty-state">
                <CardContent className="py-10 px-6 text-center">
                  <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                  {result.candidate_status === 'insufficient_data' ? (
                    <>
                      <p className="text-gray-800 font-semibold mb-2">Roll strategies cannot be computed: market quotes are missing.</p>
                      <ul className="text-sm text-gray-600 text-left inline-block list-disc list-inside space-y-1" data-testid="rollmaster-data-issues">
                        {(result.data_issues || []).map((issue, i) => <li key={i}>{issue}</li>)}
                      </ul>
                      <p className="text-xs text-gray-500 mt-3">Quotes are required so that every credit/debit is based on an executable bid or ask. Check the expiration date and strike, or try again during market hours.</p>
                    </>
                  ) : result.candidate_status === 'no_viable_candidates' ? (
                    <p className="text-gray-700">Quotes were available, but no roll candidate met the current criteria{result.candidates_evaluated ? ` (${result.candidates_evaluated} evaluated)` : ''}. Try a different goal or risk tolerance.</p>
                  ) : (
                    <p className="text-gray-600">No roll strategies could be generated for this position.</p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Available Expirations */}
          {result.available_expirations && result.available_expirations.length > 0 && (
            <Card className="glass-effect border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-sm flex items-center">
                  <Calendar className="h-4 w-4 mr-2 text-indigo-600" />
                  Available Future Expirations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {result.available_expirations.map((exp, i) => (
                    <Badge key={i} variant="outline">{exp}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Trading Chart Modal */}
      <TradingChartModal
        isOpen={isChartOpen}
        onClose={() => setIsChartOpen(false)}
        symbol={symbol}
      />
    </div>
  );
};

export default Rollmaster;
